import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function Coin3D({ size = 56, gold = false, imageUrl = '/logo-cogency.png' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 4);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (e) {
      console.warn('WebGL not available, 3D logo disabled');
      if (container) container.innerHTML = '<img src="' + imageUrl + '" style="width:100%;height:100%;object-fit:contain;" alt="Cogency" />';
      return;
    }
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = gold ? 1.25 : 1.1;
    container.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(gold ? 0xfff8e7 : 0xffffff, gold ? 1.4 : 1.2);
    keyLight.position.set(2, 3, 4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(gold ? 0xffd700 : 0xc7d2fe, gold ? 0.7 : 0.6);
    fillLight.position.set(-2, 1, 3);
    scene.add(fillLight);

    const rimLight = new THREE.SpotLight(gold ? 0xffb700 : 0x818cf8, gold ? 1.3 : 1.0);
    rimLight.position.set(0, 3, -3);
    rimLight.lookAt(0, 0, 0);
    scene.add(rimLight);

    const coinGroup = new THREE.Group();
    coinGroup.rotation.x = Math.PI / 2;
    scene.add(coinGroup);

    const COIN_RADIUS = 1.2;
    const COIN_THICKNESS = 0.16;
    const TEXTURE_SIZE = 512;

    function createCoinFaceTexture(imageUrl, flipHorizontal = false) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = TEXTURE_SIZE;
          canvas.height = TEXTURE_SIZE;
          const ctx = canvas.getContext('2d');
          const center = TEXTURE_SIZE / 2;
          const radius = center - 12;

          ctx.beginPath();
          ctx.arc(center, center, radius, 0, Math.PI * 2);
          ctx.clip();

          ctx.fillStyle = gold ? '#1a1505' : '#0f172a';
          ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

          const glow = ctx.createRadialGradient(center, center, radius * 0.75, center, center, radius);
          glow.addColorStop(0, gold ? 'rgba(255, 215, 0, 0)' : 'rgba(79, 70, 229, 0)');
          glow.addColorStop(1, gold ? 'rgba(255, 215, 0, 0.35)' : 'rgba(124, 58, 237, 0.35)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(center, center, radius, 0, Math.PI * 2);
          ctx.fill();

          let drawImg = img;
          if (flipHorizontal) {
            const flipCanvas = document.createElement('canvas');
            flipCanvas.width = img.width;
            flipCanvas.height = img.height;
            const flipCtx = flipCanvas.getContext('2d');
            flipCtx.translate(img.width, 0);
            flipCtx.scale(-1, 1);
            flipCtx.drawImage(img, 0, 0);
            drawImg = flipCanvas;
          }
          const scale = Math.min((radius * 2) / drawImg.width, (radius * 2) / drawImg.height);
          const w = drawImg.width * scale;
          const h = drawImg.height * scale;
          if (gold) {
            ctx.filter = 'sepia(0.85) saturate(1.6) brightness(1.15) hue-rotate(-5deg)';
          }
          ctx.drawImage(drawImg, center - w / 2, center - h / 2, w, h);
          ctx.filter = 'none';

          const domeGrad = ctx.createRadialGradient(
            center - radius * 0.25, center - radius * 0.25, 0,
            center, center, radius
          );
          domeGrad.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
          domeGrad.addColorStop(0.35, 'rgba(255, 255, 255, 0.05)');
          domeGrad.addColorStop(0.65, 'rgba(0, 0, 0, 0.08)');
          domeGrad.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
          ctx.fillStyle = domeGrad;
          ctx.globalCompositeOperation = 'source-atop';
          ctx.beginPath();
          ctx.arc(center, center, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalCompositeOperation = 'source-over';

          const edgeGrad = ctx.createRadialGradient(center, center, radius - 12, center, center, radius);
          edgeGrad.addColorStop(0, 'rgba(0,0,0,0)');
          edgeGrad.addColorStop(0.92, 'rgba(255,255,255,0.15)');
          edgeGrad.addColorStop(1, 'rgba(0,0,0,0.25)');
          ctx.fillStyle = edgeGrad;
          ctx.beginPath();
          ctx.arc(center, center, radius, 0, Math.PI * 2);
          ctx.fill();

          const texture = new THREE.CanvasTexture(canvas);
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
          resolve(texture);
        };
        img.onerror = () => {
          const canvas = document.createElement('canvas');
          canvas.width = TEXTURE_SIZE;
          canvas.height = TEXTURE_SIZE;
          const ctx = canvas.getContext('2d');
          const center = TEXTURE_SIZE / 2;
          const radius = center - 12;
          const grad = ctx.createRadialGradient(center, center, radius * 0.2, center, center, radius);
          grad.addColorStop(0, gold ? '#b8860b' : '#4f46e5');
          grad.addColorStop(1, gold ? '#1a1505' : '#1e1b4b');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(center, center, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = gold ? '#fff8e7' : '#e0e7ff';
          ctx.font = 'bold 120px Cinzel, serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('C', center, center);
          const texture = new THREE.CanvasTexture(canvas);
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        };
      });
    }

    let coinMesh;
    async function buildCoin() {
      const faceTexture = await createCoinFaceTexture(imageUrl, false);
      const backFaceTexture = await createCoinFaceTexture(imageUrl, true);
      const coinGeometry = new THREE.CylinderGeometry(COIN_RADIUS, COIN_RADIUS, COIN_THICKNESS, 128, 1, false);
      const uvAttribute = coinGeometry.attributes.uv;
      const posAttribute = coinGeometry.attributes.position;
      const normalAttribute = coinGeometry.attributes.normal;

      for (let i = 0; i < posAttribute.count; i++) {
        const x = posAttribute.getX(i);
        const y = posAttribute.getY(i);
        const z = posAttribute.getZ(i);
        const nx = normalAttribute.getX(i);
        const ny = normalAttribute.getY(i);
        const nz = normalAttribute.getZ(i);

        if (Math.abs(ny) > 0.9) {
          const u = (x / COIN_RADIUS) * 0.5 + 0.5;
          const v = (-z / COIN_RADIUS) * 0.5 + 0.5;
          uvAttribute.setXY(i, u, v);
        } else {
          const angle = Math.atan2(z, x);
          const u = (angle / (Math.PI * 2)) + 0.5;
          const v = (y / COIN_THICKNESS) + 0.5;
          uvAttribute.setXY(i, u, v);
        }
      }
      coinGeometry.attributes.uv.needsUpdate = true;

      const faceMaterial = new THREE.MeshStandardMaterial({
        map: faceTexture,
        metalness: gold ? 0.75 : 0.55,
        roughness: gold ? 0.28 : 0.35,
        bumpMap: faceTexture,
        bumpScale: 0.015
      });
      const backFaceMaterial = new THREE.MeshStandardMaterial({
        map: backFaceTexture,
        metalness: gold ? 0.75 : 0.55,
        roughness: gold ? 0.28 : 0.35,
        bumpMap: backFaceTexture,
        bumpScale: 0.015
      });
      const edgeMaterial = new THREE.MeshStandardMaterial({
        color: gold ? 0xd4af37 : 0x64748b,
        metalness: gold ? 0.95 : 0.9,
        roughness: gold ? 0.18 : 0.25
      });

      coinMesh = new THREE.Mesh(coinGeometry, [edgeMaterial, faceMaterial, backFaceMaterial]);
      coinGroup.add(coinMesh);

      const rimGeometry = new THREE.TorusGeometry(COIN_RADIUS, 0.025, 16, 128);
      const rimMaterial = new THREE.MeshStandardMaterial({
        color: gold ? 0xffd700 : 0x818cf8,
        metalness: 0.95,
        roughness: gold ? 0.12 : 0.15
      });
      const topRim = new THREE.Mesh(rimGeometry, rimMaterial);
      topRim.position.y = COIN_THICKNESS / 2;
      topRim.rotation.x = Math.PI / 2;
      coinGroup.add(topRim);

      const bottomRim = new THREE.Mesh(rimGeometry, rimMaterial);
      bottomRim.position.y = -COIN_THICKNESS / 2;
      bottomRim.rotation.x = Math.PI / 2;
      coinGroup.add(bottomRim);
    }
    buildCoin();

    const startTime = Date.now();
    let animationId;
    function animate() {
      animationId = requestAnimationFrame(animate);
      const t = (Date.now() - startTime) / 1000;
      coinGroup.rotation.z += 0.008;
      coinGroup.position.y = 0.1 + Math.sin(t * 0.8) * 0.03;
      renderer.render(scene, camera);
    }
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      if (coinMesh) {
        coinMesh.geometry.dispose();
        [].concat(coinMesh.material).forEach(m => m && m.dispose && m.dispose());
      }
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [size, imageUrl]);

  return <div ref={containerRef} style={{ width: size, height: size, flexShrink: 0, lineHeight: 0 }} />;
}
